import { DoNotFixMeIAmAny } from '@easy-expense/utils-typescript';

import { isValidStatementDate } from './dateParsing';

/**
 * Parses a currency value from a string, handling common formats:
 * - $123.45
 * - 123.45
 * - (123.45) for negative values
 * - 1,234.56
 * - -123.45
 */
function parseCurrencyValue(value: string | number | undefined): number | null {
  if (typeof value === 'number') {
    return Math.abs(value);
  }

  if (!value || typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  // If the value contains "/", it's likely a date, not a currency value
  if (trimmed.includes('/')) {
    return null;
  }

  // Remove currency symbols and parentheses (parentheses often indicate negative)
  let cleaned = trimmed
    .replace(/[$€£¥₹]/g, '') // Remove common currency symbols
    .replace(/,/g, '') // Remove thousand separators
    .trim();

  // Handle parentheses as negative (accounting format)
  const isNegative = cleaned.startsWith('(') && cleaned.endsWith(')');
  if (isNegative) {
    cleaned = cleaned.slice(1, -1);
  }

  // Remove any remaining non-numeric characters except decimal point and minus sign
  // Note: We don't remove "/" here because we already checked for it above
  cleaned = cleaned.replace(/[^\d.-]/g, '');

  const parsed = parseFloat(cleaned);

  if (isNaN(parsed)) {
    return null;
  }

  return Math.abs(parsed);
}

/**
 * Finds the first column (left to right) that contains date-formatted strings.
 * Analyzes sample rows to determine which column has the most valid dates.
 *
 * @param headers - Array of column headers
 * @param sampleRows - Array of data rows to analyze (should include multiple rows for accuracy)
 * @returns The header name of the date column, or undefined if none found
 */
export function autoMatchDateColumn(
  headers: string[],
  sampleRows: DoNotFixMeIAmAny[],
): string | undefined {
  if (!headers || headers.length === 0 || !sampleRows || sampleRows.length === 0) {
    return undefined;
  }

  // Analyze each column from left to right
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    if (!header) {
      continue;
    }

    // Count how many values in this column are valid dates
    let validDateCount = 0;
    let totalNonEmptyCount = 0;

    for (const row of sampleRows) {
      const value = row[header];
      if (value !== undefined && value !== null && value !== '') {
        totalNonEmptyCount++;
        if (isValidStatementDate(value)) {
          validDateCount++;
        }
      }
    }

    // Require 100% of non-empty values to be valid dates
    // Require at least 1 valid date
    if (totalNonEmptyCount > 0 && validDateCount >= 1 && validDateCount === totalNonEmptyCount) {
      return header;
    }
  }

  return undefined;
}

/**
 * Finds the column with the largest absolute currency values.
 * Analyzes sample rows to determine which column contains currency amounts.
 *
 * @param headers - Array of column headers
 * @param sampleRows - Array of data rows to analyze
 * @param excludeHeaders - Array of header names to exclude from consideration
 * @returns The header name of the total/amount column, or undefined if none found
 */
export function autoMatchTotalColumn(
  headers: string[],
  sampleRows: DoNotFixMeIAmAny[],
  excludeHeaders?: string[],
): string | undefined {
  if (!headers || headers.length === 0 || !sampleRows || sampleRows.length === 0) {
    return undefined;
  }

  const excludeSet = new Set(excludeHeaders ?? []);

  const columnStats: {
    header: string;
    maxAbsoluteValue: number;
    validValueCount: number;
    averageAbsoluteValue: number;
    textValueCount: number;
  }[] = [];

  // Analyze each column
  for (const header of headers) {
    if (!header || excludeSet.has(header)) {
      continue;
    }

    const values: number[] = [];
    let textValueCount = 0;

    for (const row of sampleRows) {
      const value = row[header];
      // Check if value looks like text (contains letters and is longer than typical numbers)
      if (typeof value === 'string' && value.length > 10 && /[A-Za-z]/.test(value)) {
        textValueCount++;
      }
      const parsed = parseCurrencyValue(value);
      if (parsed !== null) {
        values.push(parsed);
      }
    }

    // Skip columns that are mostly text (likely statement descriptors)
    if (textValueCount > sampleRows.length * 0.5) {
      continue;
    }

    if (values.length > 0) {
      const maxAbsoluteValue = Math.max(...values);
      const averageAbsoluteValue = values.reduce((sum, val) => sum + val, 0) / values.length;

      columnStats.push({
        header,
        maxAbsoluteValue,
        validValueCount: values.length,
        averageAbsoluteValue,
        textValueCount,
      });
    }
  }

  if (columnStats.length === 0) {
    return undefined;
  }

  // Sort by max absolute value (descending), then by average (descending)
  // This finds the column with the largest currency values
  columnStats.sort((a, b) => {
    if (Math.abs(b.maxAbsoluteValue - a.maxAbsoluteValue) > 0.01) {
      return b.maxAbsoluteValue - a.maxAbsoluteValue;
    }
    return b.averageAbsoluteValue - a.averageAbsoluteValue;
  });

  return columnStats[0]?.header;
}

/**
 * Finds the statement descriptor column by matching common header names.
 * Looks for columns that typically contain merchant/transaction descriptions.
 *
 * @param headers - Array of column headers
 * @param dateHeader - The detected date header (to exclude from matching)
 * @param totalHeader - The detected total header (to exclude from matching)
 * @returns The header name of the statement descriptor column, or undefined if none found
 */
export function autoMatchStatementDescriptorColumn(
  headers: string[],
  dateHeader?: string,
  totalHeader?: string,
): string | undefined {
  if (!headers || headers.length === 0) {
    return undefined;
  }

  // Common names for statement descriptor columns (in order of priority)
  const descriptorPatterns = [
    /^statement\s*descriptor$/i,
    /^transaction\s*description$/i,
    /^description$/i,
    /^merchant$/i,
    /^merchant\s*name$/i,
    /^payee$/i,
    /^name$/i,
    /^transaction\s*details$/i,
    /^details$/i,
    /^vendor$/i,
    /^store$/i,
    /^business\s*name$/i,
  ];

  // First, try exact matches (case-insensitive)
  for (const pattern of descriptorPatterns) {
    for (const header of headers) {
      if (!header) {
        continue;
      }
      // Skip if this is the date or total header
      if (header === dateHeader || header === totalHeader) {
        continue;
      }
      if (pattern.test(header.trim())) {
        return header;
      }
    }
  }

  // If no exact match, look for columns that contain descriptor-related keywords
  const keywordPatterns = ['descriptor', 'description', 'merchant', 'payee', 'transaction'];
  for (const keyword of keywordPatterns) {
    for (const header of headers) {
      if (!header) {
        continue;
      }
      // Skip if this is the date or total header
      if (header === dateHeader || header === totalHeader) {
        continue;
      }
      const lowerHeader = header.toLowerCase();
      if (
        lowerHeader.includes(keyword) &&
        !lowerHeader.includes('date') &&
        !lowerHeader.includes('total')
      ) {
        return header;
      }
    }
  }

  return undefined;
}

/**
 * Auto-matches date, total, and statement descriptor columns using smart detection
 *
 * @param headers - Array of column headers
 * @param sampleRows - Array of data rows to analyze
 * @returns Object with dateHeader, totalHeader, and statementDescriptorHeader (may be undefined)
 */
export function autoMatchColumns(
  headers: string[],
  sampleRows: DoNotFixMeIAmAny[],
): {
  dateHeader: string | undefined;
  totalHeader: string | undefined;
  statementDescriptorHeader: string | undefined;
} {
  // First detect date column
  const dateHeader = autoMatchDateColumn(headers, sampleRows);

  // Then detect statement descriptor (excluding date)
  const statementDescriptorHeader = autoMatchStatementDescriptorColumn(headers, dateHeader);

  // Finally detect total column (excluding both date and statement descriptor)
  const excludeHeaders = [dateHeader, statementDescriptorHeader].filter(
    (h): h is string => h !== undefined,
  );
  const totalHeader = autoMatchTotalColumn(headers, sampleRows, excludeHeaders);

  return {
    dateHeader,
    totalHeader,
    statementDescriptorHeader,
  };
}
